import { getPlatform } from '../../platform';
// Default test wallet address (Solana devnet address)
export const DEFAULT_TEST_ADDRESS = 'HKbZbRR7jWWR5VRN8KFjvTCHEzJQgameYxKQxh2gPoof';
// Session storage key to track if user explicitly called login()
const EXPLICIT_LOGIN_KEY = 'mock-auth-explicit-login';
/**
 * MockAuthProvider bypasses real wallet authentication for testing purposes.
 * Uses session storage to simulate logged-in user, with fallback to default test address.
 *
 * This is enabled by passing mockAuth: true to init()
 */
export class MockAuthProvider {
    /**
     * Mock login - reads user from session storage or uses default test address
     */
    async login() {
        let address = getPlatform().sessionStorage.getItem('test-user-address');
        if (!address) {
            address = DEFAULT_TEST_ADDRESS;
            // Store default address in session storage so it gets used in API calls
            getPlatform().sessionStorage.setItem('test-user-address', DEFAULT_TEST_ADDRESS);
            console.log('[MockAuth] Using default test address:', address);
        }
        // Mark that user explicitly called login() - allows restoreSession on refresh
        getPlatform().sessionStorage.setItem(EXPLICIT_LOGIN_KEY, 'true');
        console.log('[MockAuth] Mock login successful:', address);
        return {
            address,
            provider: this
        };
    }
    /**
     * Mock transaction - returns fake signature without actual blockchain interaction
     * The real transaction will be handled by the backend using test headers
     */
    async runTransaction(evmTransactionData, solTransactionData, options) {
        const address = getPlatform().sessionStorage.getItem('test-user-address') || DEFAULT_TEST_ADDRESS;
        // Generate a fake transaction signature
        const fakeSignature = `mock_tx_${Math.random().toString(36).substring(2, 15)}`;
        console.log('[MockAuth] Mock transaction executed:', {
            address,
            signature: fakeSignature,
            evmTx: evmTransactionData,
            solTx: solTransactionData
        });
        return {
            transactionSignature: fakeSignature,
            signedTransaction: fakeSignature,
            blockNumber: 0,
            gasUsed: '0'
        };
    }
    /**
     * Mock message signing
     */
    async signMessage(message) {
        const address = getPlatform().sessionStorage.getItem('test-user-address') || DEFAULT_TEST_ADDRESS;
        // Generate a fake signature
        const fakeSignature = `mock_sig_${Math.random().toString(36).substring(2, 15)}`;
        console.log('[MockAuth] Mock message signed:', {
            address,
            message,
            signature: fakeSignature
        });
        return fakeSignature;
    }
    async signMessageMock(message) {
        const address = getPlatform().sessionStorage.getItem('test-user-address') || DEFAULT_TEST_ADDRESS;
        // Generate a fake signature
        const fakeSignature = `mock_sig_${Math.random().toString(36).substring(2, 15)}`;
        console.log('[MockAuth] Mock message signed:', {
            address,
            message,
            signature: fakeSignature
        });
        return fakeSignature;
    }
    /**
     * Mock transaction signing - returns the transaction as-is (no actual signing)
     */
    async signTransaction(transaction) {
        console.log('[MockAuth] Mock transaction signing (no-op)');
        return transaction;
    }
    /**
     * Sign and submit transaction - not supported in mock environment.
     * See the real providers (PhantomWalletProvider, PrivyWalletProvider, SolanaKeypairProvider)
     * for the full implementation with blockhash handling and feePayer support.
     */
    async signAndSubmitTransaction(_transaction, _feePayer) {
        throw new Error('signAndSubmitTransaction is not supported in the mock environment');
    }
    /**
     * Restore session - only restores if user previously called login() explicitly.
     * This prevents auto-login on first page load, but allows session persistence after login.
     */
    async restoreSession() {
        const explicitlyLoggedIn = getPlatform().sessionStorage.getItem(EXPLICIT_LOGIN_KEY);
        if (!explicitlyLoggedIn) {
            console.log('[MockAuth] No explicit login found. Call login() to authenticate.');
            return null;
        }
        const address = getPlatform().sessionStorage.getItem('test-user-address');
        if (!address) {
            console.log('[MockAuth] No previous session found');
            return null;
        }
        console.log('[MockAuth] Session restored:', address);
        return {
            address,
            provider: this
        };
    }
    /**
     * Mock logout - clears test user from session storage
     */
    async logout() {
        getPlatform().sessionStorage.removeItem('test-user-address');
        getPlatform().sessionStorage.removeItem('test-user-id');
        getPlatform().sessionStorage.removeItem(EXPLICIT_LOGIN_KEY);
        console.log('[MockAuth] Mock logout successful');
    }
    /**
     * Get native wallet methods (not needed for mock auth)
     */
    async getNativeMethods() {
        return {
        // Return empty object - no native methods needed for mock auth
        };
    }
}
